import {
  authApi,
  clearTokens,
  setTokens,
  refreshSession,
  isTransientFailure,
  SessionUnknownError,
} from './apiClient'
import { storage } from './platform/storage'
import { clearReadState } from './readState'
import { resetSocket } from './socketClient'
import type { AuthLoginRequest, AuthLoginResponse, User } from './types'

/**
 * Профиль последнего вошедшего — чтобы установленное приложение,
 * запущенное без сети, знало, кого показывать.
 *
 * Токенов здесь нет и быть не может: access живёт только в памяти,
 * refresh — в httpOnly-куке. Это не доступ, а ответ на вопрос «кто вошёл»,
 * поэтому в офлайне по нему открывается кабинет и читалка из кэша,
 * но ни один запрос к серверу такой профиль не авторизует.
 */
const CACHED_USER_KEY = 'lightchat_last_user'

function cacheUser(user: User): void {
  try {
    storage.set(CACHED_USER_KEY, JSON.stringify(user))
  } catch {
    // Приватный режим или переполненное хранилище — не повод ронять вход
  }
}

function getCachedUser(): User | null {
  const raw = storage.get(CACHED_USER_KEY)
  if (!raw) return null
  try {
    return JSON.parse(raw) as User
  } catch {
    storage.remove(CACHED_USER_KEY)
    return null
  }
}

function clearCachedUser(): void {
  storage.remove(CACHED_USER_KEY)
}

/** Шаг 1 регистрации: бэкенд отправляет код на почту. */
export async function register(
  data: AuthLoginRequest & { source?: string; sourcePage?: string; referralCode?: string },
): Promise<{ email: string }> {
  const res = await authApi.register(data)
  return { email: res.data.data.email as string }
}

/** Шаг 2: подтверждение кода создаёт пользователя и выдаёт токены. */
export async function verifyRegistrationCode(email: string, code: string): Promise<User> {
  const res = await authApi.verifyCode({ email, code, type: 'registration' })
  const body = res.data.data as AuthLoginResponse
  setTokens({ accessToken: body.tokens.accessToken, refreshToken: body.tokens.refreshToken })
  return body.user
}

export async function forgotPassword(data: {
  email: string
  newPassword: string
}): Promise<{ email: string }> {
  const res = await authApi.forgotPassword(data)
  return { email: res.data.data.email as string }
}

export async function verifyPasswordResetCode(email: string, code: string): Promise<void> {
  await authApi.verifyCode({ email, code, type: 'password_reset' })
}

export async function login(data: AuthLoginRequest): Promise<User> {
  const res = await authApi.login(data)
  const body = res.data.data as AuthLoginResponse
  setTokens({ accessToken: body.tokens.accessToken, refreshToken: body.tokens.refreshToken })
  return body.user
}

export async function fetchMe(): Promise<User> {
  const res = await authApi.me()
  return res.data.data.user as User
}

/**
 * Восстановление сессии при загрузке страницы.
 *
 * В приложении токен лежал в SecureStore и переживал перезапуск. В браузере
 * access-токен живёт только в памяти и после перезагрузки страницы его нет,
 * поэтому сессия поднимается из httpOnly-куки через /auth/refresh.
 * Отсутствие сессии — это норма, а не ошибка.
 */
export async function restoreSession(): Promise<User | null> {
  try {
    const token = await refreshSession()
    if (!token) {
      // Сервер ответил и сессии не признал — забываем, кто входил
      clearCachedUser()
      return null
    }
  } catch (error) {
    // Связи нет или сервер лёг. Это не «сессия кончилась», и выбрасывать
    // человека на экран логина нельзя: офлайн он там ничего не сделает,
    // а Библия лежит в кэше и читается. Проверим, когда связь появится.
    if (error instanceof SessionUnknownError) return getCachedUser()
    clearCachedUser()
    return null
  }

  try {
    const user = await fetchMe()
    cacheUser(user)
    return user
  } catch (error) {
    // Сессия уже подтверждена обновлением токена — упавший профиль
    // означает сбой связи или сервера, а не отсутствие доступа
    if (isTransientFailure(error)) return getCachedUser()
    clearTokens()
    clearCachedUser()
    return null
  }
}

export async function logout(): Promise<void> {
  try {
    await authApi.logout()
  } catch {
    // Сервер мог не ответить — локальный выход всё равно должен состояться
  }
  clearTokens()
  // Веб часто открывают на общем компьютере: имя вошедшего, отметки
  // прочитанности и живой сокет не должны достаться следующему.
  clearCachedUser()
  clearReadState()
  resetSocket()
}
